/**
 * Decision Engine - Product Info Management
 *
 * Manages product information key-value pairs for the decision engine.
 * Simple field/value/notes structure for product metadata.
 */

import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { SectionHeader } from '@client/components/_ui/data-display/SectionHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Badge, Form, Input, InputNumber, Switch } from 'antd';
import { Info, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

type ProductInfoFormInput = {
    field: string;
    value: string | null;
    notes: string | null;
    sortOrder: number;
    isActive: boolean;
};

const FormItem = Form.Item<ProductInfoFormInput>;

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/product-info',
)({
    staticData: {
        metadata: {
            title: 'Product info',
        },
    },
    component: ProductInfoManagement,
});

function ProductInfoManagement() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<ProductInfoFormInput>();

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    // The form mounts with the drawer, so the values it opens on are held here and passed as `initialValues`.
    const initialValuesRef = useRef<ProductInfoFormInput | null>(null);
    const formDirty = useFormDirty();
    const closeRef = useRef<(() => void) | null>(null);
    const formId = 'product-info-form';

    // Fetch product info
    const { data, isLoading } = trpc.claim.decisionEngine.admin.listProductInfo.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );
    const productInfoItems = data?.rows ?? [];

    // Mutations
    const { mutate: createProductInfo, isPending: isCreating } =
        trpc.claim.decisionEngine.admin.createProductInfo.useMutation({
            onSuccess() {
                message.success('Product info created.');
                void trpcUtils.claim.decisionEngine.admin.listProductInfo.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: updateProductInfo, isPending: isUpdating } =
        trpc.claim.decisionEngine.admin.updateProductInfo.useMutation({
            onSuccess() {
                message.success('Product info updated.');
                void trpcUtils.claim.decisionEngine.admin.listProductInfo.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: deleteProductInfo } = trpc.claim.decisionEngine.admin.deleteProductInfo.useMutation({
        onSuccess() {
            message.success('Product info deleted.');
            void trpcUtils.claim.decisionEngine.admin.listProductInfo.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Table columns
    const columnHelper = createColumnHelper<(typeof productInfoItems)[number]>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('field', {
                id: 'field',
                header: 'Field',
                width: 200,
            }),
            columnHelper.accessor('value', {
                id: 'value',
                header: 'Value',
                width: 300,
                cell: ({ row }) => row.value ?? <span className="text-muted-foreground">—</span>,
            }),
            columnHelper.accessor('notes', {
                id: 'notes',
                header: 'Notes',
                width: 250,
                cell: ({ row }) => row.notes ?? <span className="text-muted-foreground">—</span>,
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                width: 100,
                cell: ({ row }) => (
                    <Badge status={row.isActive ? 'success' : 'default'} text={row.isActive ? 'Active' : 'Inactive'} />
                ),
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit onClick={() => openEditDrawer(row)} />
                        <Button.Delete
                            confirmTitle="Delete product info"
                            confirmDescription="Are you sure you want to delete this product info entry?"
                            onConfirm={(onSuccess) =>
                                deleteProductInfo(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [columnHelper, deleteProductInfo],
    );

    const tableProps = usePrismaTable({
        data: productInfoItems,
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: { position: 'none' },
        emptyState: { noRecordsTitle: 'No product information yet' },
    });

    // Drawer handlers
    const openCreateDrawer = () => {
        setEditingId(null);
        initialValuesRef.current = {
            field: '',
            value: null,
            notes: null,
            sortOrder: 0,
            isActive: true,
        };
        setDrawerOpen(true);
    };

    const openEditDrawer = (item: (typeof productInfoItems)[number]) => {
        setEditingId(item.id);
        initialValuesRef.current = {
            field: item.field,
            value: item.value,
            notes: item.notes,
            sortOrder: item.sortOrder,
            isActive: item.isActive,
        };
        setDrawerOpen(true);
    };

    // Runs after the closing animation, whether the drawer was dismissed or closed after a save.
    const resetDrawer = () => {
        form.resetFields();
        formDirty.markClean();
        initialValuesRef.current = null;
        setDrawerOpen(false);
        setEditingId(null);
    };

    const onFinish = (values: ProductInfoFormInput) => {
        const data = {
            field: values.field,
            value: values.value,
            notes: values.notes,
            sortOrder: values.sortOrder,
            isActive: values.isActive,
        };

        if (editingId) {
            updateProductInfo({ id: editingId, data });
        } else {
            createProductInfo({ productTypeId: BigInt(productTypeId!), data });
        }
    };

    return (
        <PageController route={Route}>
            <div className="mb-6 flex items-start gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                    <Info className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                    <SectionHeader as="h3" size="small" title="Product information" className="mb-1" />
                    <p className="text-muted-foreground text-sm">
                        Key-value pairs providing product metadata to the decision engine. This information gives the AI
                        context about the product type when making coverage and classification decisions.
                    </p>
                </div>
            </div>
            <TableToolbar
                actions={
                    <Button type="primary" icon={<Plus />} onClick={openCreateDrawer}>
                        Add product info
                    </Button>
                }
            />
            <PrismaTable {...tableProps} />

            {drawerOpen && (
                <OverlayPage
                    title={editingId ? 'Edit product info' : 'Add product info'}
                    size="small"
                    isDirty={formDirty.isDirty}
                    afterClose={resetDrawer}
                    footer={({ requestClose }) => (
                        <DrawerFooter
                            onCancel={requestClose}
                            formId={formId}
                            okText={editingId ? 'Save changes' : 'Create'}
                            okLoading={isCreating || isUpdating}
                        />
                    )}
                >
                    {({ onClose }) => {
                        closeRef.current = onClose;
                        return (
                            <Form<ProductInfoFormInput>
                                form={form}
                                {...formDirty.formProps}
                                layout="vertical"
                                onFinish={onFinish}
                                id={formId}
                                clearOnDestroy
                                initialValues={initialValuesRef.current ?? undefined}
                            >
                                <FormItem
                                    label="Field"
                                    name="field"
                                    rules={[{ required: true, message: 'Field name is required' }]}
                                >
                                    <Input placeholder="e.g., Product name" />
                                </FormItem>

                                <FormItem label="Value" name="value">
                                    <Input placeholder="e.g., Professional indemnity insurance" />
                                </FormItem>

                                <FormItem label="Notes" name="notes">
                                    <Input placeholder="Additional notes about this field" />
                                </FormItem>

                                <div className="mt-4 border-separator border-t pt-4">
                                    <FormItem label="Sort order" name="sortOrder" tooltip="Lower numbers appear first">
                                        <InputNumber min={0} className="w-full" />
                                    </FormItem>

                                    <FormItem label="Active" name="isActive" valuePropName="checked">
                                        <Switch />
                                    </FormItem>
                                </div>
                            </Form>
                        );
                    }}
                </OverlayPage>
            )}
        </PageController>
    );
}
